import styles from './index.module.css'

import React, { useEffect, useRef } from 'react'

import {
  BRIEF_INBOX,
  CUSTOM_CHANNEL,
  INBOX_TYPES,
} from '@disco/constants/GroupTypes'
import { getGroupsWithNesting } from '@disco/state-management/selectors/groups'

import { FilterComponentProps } from 'types/filter-page'
import type { Group, IndentedNestedGroup } from 'types/group'

import { Flex, Separator, Text, TextField } from 'components/ds'

import { useSelector } from 'hooks/use-redux'

import { CheckboxContainer, CheckboxItem } from '../components'
import CheckboxRow from '../components/checkbox-row'
import AccordionCheckbox from './accordion-checkbox'
import { useDebouncedGroupSearch, useFilteredUnselectedGroups } from './hooks'
import { getFilteredGroups } from './utils'

type GroupFilterType = 'inboxes' | 'channels' | 'briefs'

const GroupForm = ({
  value,
  onChange,
  operator = 'equals',
  onPress,
  label,
}: FilterComponentProps) => {
  const groups = useSelector(state => getGroupsWithNesting(state))

  // Determine group type from label
  const groupType: GroupFilterType =
    label === 'Channels'
      ? 'channels'
      : label === 'Briefs'
      ? 'briefs'
      : 'inboxes'

  // Filter groups to get only the specified types
  const groupsByType = groups.filter(group => {
    switch (groupType) {
      case 'channels':
        return [CUSTOM_CHANNEL].includes(group.group_type)
      case 'briefs':
        return group.group_type === BRIEF_INBOX
      case 'inboxes':
      default:
        return INBOX_TYPES.filter(type => type !== BRIEF_INBOX).includes(
          group.group_type,
        )
    }
  })

  const pendingSearchRef = useRef<boolean>(false)
  const isGroupsChannels = groupsByType.some(
    group => group.group_type === CUSTOM_CHANNEL,
  )

  const {
    searchTerm,
    currentGroups,
    handleSearchChange,
    handleToggle,
    isSearching,
  } = useDebouncedGroupSearch(groupsByType)

  const filteredGroups = searchTerm
    ? getFilteredGroups(currentGroups, searchTerm)
    : currentGroups.slice(0, 5)

  // Trigger search when value changes and a search is pending
  useEffect(() => {
    if (pendingSearchRef.current && onPress) {
      onPress()
      pendingSearchRef.current = false
    }
  }, [value, onPress])

  // Check if this is an "All" selection (has the special marker)
  const isAllSelection =
    value &&
    Array.isArray(value) &&
    value.length > 0 &&
    value[0]?._isAllSelection

  const allGroupsSelectionName = `All ${groupType}`
  const isAllGroupsSearched =
    searchTerm?.length &&
    allGroupsSelectionName.toLowerCase().includes(searchTerm.toLowerCase())

  // Get selected groups (excluding those with _isAllSelection marker)
  const selectedGroups =
    value && Array.isArray(value) && !isAllSelection ? value : []

  const unselectedGroups = useFilteredUnselectedGroups(
    filteredGroups,
    selectedGroups,
    searchTerm,
  )
  const isGroupSelected = (group: any) => {
    const selectedIds = new Set(selectedGroups.map(g => g.id))
    return selectedIds.has(group.id)
  }

  // "All Groups" checkbox component
  const AllGroupsCheckbox = () => {
    if (isAllGroupsSearched || !searchTerm.length) {
      return (
        <CheckboxRow
          checked={isAllSelection}
          onCheckedChange={checked => handleAllGroupsCheck(checked)}
          onClick={(event: React.MouseEvent) => {
            handleAllGroupsCheck(!isAllSelection, event)
          }}
          className={styles.GroupFilter__checkboxRow}
        >
          {allGroupsSelectionName}
        </CheckboxRow>
      )
    }
  }

  const AccordionCheckBox = ({ groups }) => {
    return (
      <AccordionCheckbox
        groups={groups}
        handleSelection={handleGroupCheck}
        searchValue={searchTerm}
        onSelected={isGroupSelected}
        handleToggle={handleToggle}
      />
    )
  }

  const GroupCheckboxList = ({
    groups,
    isChecked,
  }: {
    groups: Group[]
    isChecked: boolean
  }) => (
    <Flex direction="column" width="100%">
      {groups.map(group => {
        const groupName = group.internal_name || group.name || ''

        return (
          <CheckboxContainer key={group.id} containerName={groupName}>
            <CheckboxItem
              item={group}
              isChecked={isChecked}
              onSelected={isGroupSelected}
              handleSelection={handleGroupCheck}
              searchValue={searchTerm}
              indentedSpace={0}
            />
          </CheckboxContainer>
        )
      })}
    </Flex>
  )

  const handleGroupCheck = (
    checked: string | boolean,
    group: IndentedNestedGroup,
    event?: React.MouseEvent,
  ) => {
    // Prevent event bubbling to avoid closing the popover
    if (event) {
      event.stopPropagation()
    }

    const selectedGroups = checked
      ? [...(value ?? []), group]
      : (value ?? []).filter((v: Group) => v.id !== group.id)
    onChange(selectedGroups?.length ? selectedGroups : undefined, operator)

    // Don't trigger search immediately for individual checkboxes - let user select multiple
    // Search will be triggered when user clicks outside the popover
  }

  const handleAllGroupsCheck = (
    checked: string | boolean,
    event?: React.MouseEvent,
  ) => {
    if (event) {
      event.stopPropagation()
    }

    // A single marker object is enough to encode the "All" selection: every
    // consumer only inspects value[0], and the backend is told via the
    // search_in.all_channels / all_inboxes flags rather than group ids.
    const selectedGroups = checked
      ? [
          {
            name: `All ${groupType}`,
            group_type: groupType,
            _isAllSelection: true,
          },
        ]
      : undefined

    onChange(selectedGroups, operator)

    // Mark that we want to trigger search after value updates (only when checking)
    if (checked && onPress) {
      pendingSearchRef.current = true
    }
  }

  // Get placeholder text based on group type
  const placeholder = `Search ${groupType}...`
  const isNoResults =
    !isAllSelection &&
    Boolean(!isAllGroupsSearched) &&
    filteredGroups.length === 0 &&
    searchTerm.length > 0 &&
    !isSearching

  return (
    <Flex direction="column" width="250px" position="relative">
      <Flex
        direction="column"
        width="100%"
        className={styles.GroupFilter__searchContainer}
      >
        <Flex
          width="100%"
          direction="column"
          pt="4"
          className={styles.GroupFilter__searchField}
        >
          <TextField.Root
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder={placeholder}
            type="search"
            disabled={isAllSelection}
          />
        </Flex>
      </Flex>

      {/* No results message */}
      {isNoResults && (
        <Text size="2" color="gray" mb="1">
          No results for &ldquo;{searchTerm}&rdquo;
        </Text>
      )}

      {/* All Groups Checkbox - Show at top when selected */}
      {isAllSelection && groupsByType.length > 0 && <AllGroupsCheckbox />}

      <Flex direction="column" width="100%">
        {/* Selected Groups Section - Show when there are selected groups */}
        {!isAllSelection && selectedGroups.length > 0 && (
          <GroupCheckboxList groups={selectedGroups} isChecked={true} />
        )}

        {/* Separator */}
        {(!isAllSelection &&
          Boolean(isAllGroupsSearched) &&
          (selectedGroups.length > 0 || unselectedGroups.length > 0)) ||
          (selectedGroups.length > 0 && unselectedGroups.length > 0 && (
            <Separator size="4" my="2" />
          ))}

        {/* Suggested Title */}
        {!isAllSelection && groupsByType.length > 0 && !searchTerm.trim() && (
          <Text
            size="2"
            color="gray"
            className={styles.GroupFilter__suggestedTitle}
          >
            Suggested
          </Text>
        )}

        {/* All Groups Checkbox - Always show when not "All" selected and groups exist */}
        {!isAllSelection && <AllGroupsCheckbox />}

        {!isAllSelection && unselectedGroups.length > 0 && (
          <>
            {isGroupsChannels ? (
              <AccordionCheckBox groups={unselectedGroups} />
            ) : (
              <GroupCheckboxList groups={unselectedGroups} isChecked={false} />
            )}
          </>
        )}
      </Flex>
    </Flex>
  )
}

export default GroupForm
